// Comprehensive branded claim package PDF generator
// Cover • Readiness Brief • Condition Matrix • Evidence Index • Missing Evidence Plan
// • Statement Packet • C&P Prep Sheets • VA Math Summary • Disclaimer • Page numbers • Watermark

import { PDFDocument, StandardFonts, rgb, degrees } from "pdf-lib";
import {
  computePillarStatus,
  getEvidenceGaps,
  calculateReadiness,
  estimateVACompensation,
  estimateHigh3Retirement,
  money,
  buildStatementText,
  combineRatings,
} from "./claimUtils";
import { evidenceElements, bodySystems, getConditionResources } from "./claimData";

// ---- Brand tokens ----
const BRAND_NAME = "BeTheNexus.com";
const BRAND_TAGLINE = "Powered by ClaimOps VA";
const PAGE_W = 612;
const PAGE_H = 792;
const M = 48; // page margin

// ClaimOps blue palette
const C_BRAND = rgb(0.11, 0.30, 0.85);      // #1D4ED8
const C_BRAND_DARK = rgb(0.06, 0.09, 0.16); // #0F172A
const C_TEXT = rgb(0.06, 0.09, 0.16);
const C_MUTED = rgb(0.39, 0.45, 0.55);
const C_BORDER = rgb(0.85, 0.88, 0.92);
const C_BG_SOFT = rgb(0.97, 0.98, 0.99);
const C_RED = rgb(0.60, 0.10, 0.10);
const C_AMBER = rgb(0.57, 0.27, 0.05);
const C_BLUE = rgb(0.12, 0.25, 0.62);
const C_GREEN = rgb(0.02, 0.37, 0.27);

const STATUS_COLOR = {
  have: C_GREEN,
  need: C_RED,
  missing: C_RED,
  weak: C_AMBER,
  adequate: C_BLUE,
  strong: C_GREEN,
};
const STATUS_BG = {
  have: rgb(0.92, 0.99, 0.95),
  need: rgb(0.99, 0.94, 0.94),
  missing: rgb(0.99, 0.94, 0.94),
  weak: rgb(0.99, 0.97, 0.91),
  adequate: rgb(0.93, 0.96, 1.0),
  strong: rgb(0.92, 0.99, 0.95),
};

const DISCLAIMER_PARAGRAPHS = [
  "CLAIMOPS IS NOT A LAW FIRM. ClaimOps VA is a digital planning and organization tool only. It is not a law firm, claims agent, or legal services provider. No attorney-client relationship is created by using this app.",
  "CLAIMOPS DOES NOT GUARANTEE RATINGS. No tool, calculation, or estimate produced by ClaimOps VA guarantees any rating, decision, or outcome from the Department of Veterans Affairs. Every claim is decided by the VA based on the official record.",
  "CLAIMOPS DOES NOT REPLACE A VSO, ATTORNEY, OR ACCREDITED AGENT. For legal representation, you should work with a VA-accredited Veterans Service Organization (VSO), attorney, or claims agent. ClaimOps VA does not represent you before the VA or file claims on your behalf.",
  "CLAIMOPS HELPS VETERANS ORGANIZE, PREPARE, AND ADVOCATE FOR THEMSELVES. Our purpose is to put your evidence, conditions, statements, and exam prep in one place so you can communicate clearly with the VA, your VSO, or your representative.",
  "NO LIABILITY. The creator(s) of this application accept no liability whatsoever for any outcomes, decisions, errors, or consequences resulting from the use of this tool. You use this application entirely at your own risk.",
  "NOT OFFICIAL VA FORMS. This document is an unofficial planning tool only. It is NOT an official VA form and must NOT be submitted to the VA as-is. After organizing your information here, you must complete and submit the correct official VA forms available at VA.gov/find-forms.",
  "NOT LEGAL, MEDICAL, OR PROFESSIONAL ADVICE. ClaimOps VA does not provide legal advice, medical advice, or any form of professional counsel. We are not affiliated with the Department of Veterans Affairs, any Veterans Service Organization (VSO), accredited claims agent, attorney, or medical professional.",
  "VERIFY EVERYTHING. All rating criteria, diagnostic codes, compensation rates, form versions, deadlines, and regulatory references must be independently verified against official sources (VA.gov, eCFR.gov) before taking any action.",
  "THIS DOCUMENT IS FOR PLANNING AND ORGANIZATION ONLY. It was generated to help organize claim information in one place. It does not file claims, communicate with the VA, or make any representations on your behalf.",
];

// ---- helpers ----
function wrap(text, charsPerLine) {
  const words = String(text || "").replace(/\r\n?/g, "\n").split("\n").flatMap((para, idx, arr) => {
    const items = para.split(" ").filter(Boolean);
    const rows = [];
    let row = "";
    items.forEach((w) => {
      if ((row + " " + w).trim().length > charsPerLine) { rows.push(row); row = w; }
      else row = (row + " " + w).trim();
    });
    if (row) rows.push(row);
    if (rows.length === 0) rows.push("");
    if (idx < arr.length - 1) rows.push(""); // paragraph break
    return rows;
  });
  return words;
}

function sanitizeText(text) {
  // pdf-lib's WinAnsi encoding only handles a limited character set.
  // Strip or replace common Unicode glyphs that would cause encodeUnicode errors.
  return String(text ?? "")
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2026/g, "...")
    .replace(/[\u2022\u00B7]/g, "*")
    .replace(/\u00A0/g, " ")
    // Strip everything else outside basic WinAnsi range
    .replace(/[^\x09\x0A\x0D\x20-\x7E\xA0-\xFF]/g, "");
}

class PdfCanvas {
  constructor(doc, font, bold) {
    this.doc = doc;
    this.font = font;
    this.bold = bold;
    this.pages = [];
    this.y = 0;
    this.page = null;
    this.section = "";
  }
  newPage(section) {
    this.page = this.doc.addPage([PAGE_W, PAGE_H]);
    this.pages.push({ page: this.page, section: section ?? this.section });
    this.section = section ?? this.section;
    this.y = PAGE_H - M;
    return this.page;
  }
  ensure(space, section) {
    if (!this.page || this.y - space < M + 30) this.newPage(section);
  }
  text(value, x, y, opts = {}) {
    const { size = 10, bold = false, color = C_TEXT, font, maxWidth } = opts;
    const useFont = font || (bold ? this.bold : this.font);
    let str = sanitizeText(value);
    if (maxWidth && str) {
      // Truncate w/ ellipsis if too wide. Use char-level shrink to avoid infinite loops.
      let safety = 200;
      while (safety-- > 0 && str.length > 1 && useFont.widthOfTextAtSize(str, size) > maxWidth) {
        // Remove enough characters that the suffix "..." doesn't undo our progress.
        str = str.slice(0, -3);
      }
      if (str !== sanitizeText(value)) str = str.replace(/\.+$/, "") + "...";
    }
    this.page.drawText(str, { x, y, size, font: useFont, color });
  }
  drawLine(x1, y1, x2, y2, color = C_BORDER, thickness = 0.7) {
    this.page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness, color });
  }
  rect(x, y, w, h, opts = {}) {
    const { fill, stroke, strokeW = 0.7 } = opts;
    this.page.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: stroke, borderWidth: stroke ? strokeW : 0 });
  }
  heading(label, color = C_BRAND_DARK) {
    this.ensure(40);
    this.rect(M, this.y - 4, PAGE_W - M * 2, 1.4, { fill: color });
    this.y -= 18;
    this.text(label, M, this.y, { size: 13, bold: true, color });
    this.y -= 18;
  }
  sectionTitle(label) {
    this.ensure(28);
    this.text(label.toUpperCase(), M, this.y, { size: 9, bold: true, color: C_BRAND });
    this.y -= 14;
    this.drawLine(M, this.y + 4, M + 60, this.y + 4, C_BRAND, 1.2);
    this.y -= 4;
  }
  para(text, opts = {}) {
    const { size = 10, color = C_TEXT, bold = false, width = 95, indent = 0 } = opts;
    const lines = wrap(text, width);
    lines.forEach((line) => {
      this.ensure(size + 4);
      this.text(line, M + indent, this.y, { size, color, bold });
      this.y -= size + 3;
    });
  }
  bullet(text, opts = {}) {
    const { size = 9, color = C_TEXT } = opts;
    this.ensure(size + 4);
    this.text("\u2022", M + 8, this.y, { size, color: C_BRAND, bold: true });
    const lines = wrap(text, 92);
    lines.forEach((line, idx) => {
      if (idx > 0) this.ensure(size + 3);
      this.text(line, M + 20, this.y, { size, color });
      this.y -= size + 3;
    });
  }
  statusPill(text, status, x, y) {
    const fontSize = 8;
    const padX = 6;
    const w = this.bold.widthOfTextAtSize(text, fontSize) + padX * 2;
    this.page.drawRectangle({ x, y: y - 2, width: w, height: 13, color: STATUS_BG[status], borderColor: STATUS_COLOR[status], borderWidth: 0.6 });
    this.text(text, x + padX, y + 1, { size: fontSize, bold: true, color: STATUS_COLOR[status] });
    return w;
  }
  checkboxItem(checked, label, opts = {}) {
    const { size = 9, indent = 0 } = opts;
    this.ensure(size + 6);
    const boxSize = 9;
    const boxX = M + indent;
    const boxY = this.y - 1;
    // Draw checkbox
    this.page.drawRectangle({
      x: boxX, y: boxY, width: boxSize, height: boxSize,
      color: checked ? C_GREEN : undefined,
      borderColor: checked ? C_GREEN : C_BORDER, borderWidth: 0.8,
    });
    if (checked) {
      // White check mark using two lines
      this.page.drawLine({ start: { x: boxX + 1.6, y: boxY + 4 }, end: { x: boxX + 3.6, y: boxY + 1.8 }, thickness: 1.2, color: rgb(1, 1, 1) });
      this.page.drawLine({ start: { x: boxX + 3.6, y: boxY + 1.8 }, end: { x: boxX + 7.4, y: boxY + 7 }, thickness: 1.2, color: rgb(1, 1, 1) });
    }
    const labelX = boxX + boxSize + 6;
    const lines = wrap(label, 86 - Math.floor(indent / 6));
    lines.forEach((line, idx) => {
      if (idx > 0) this.ensure(size + 3);
      this.text(line, labelX, this.y, { size, color: checked ? C_TEXT : C_TEXT, bold: false });
      this.y -= size + 3;
    });
  }
}

function drawHeader(canvas, info) {
  const { page } = canvas;
  // Top brand strip
  page.drawRectangle({ x: 0, y: PAGE_H - 28, width: PAGE_W, height: 28, color: C_BRAND_DARK });
  page.drawRectangle({ x: 0, y: PAGE_H - 30, width: PAGE_W, height: 2, color: C_BRAND });
  // Brand badge "BN"
  page.drawRectangle({ x: M, y: PAGE_H - 22, width: 18, height: 14, color: C_BRAND });
  canvas.text("BN", M + 4, PAGE_H - 19, { size: 8, bold: true, color: rgb(1, 1, 1) });
  canvas.text(BRAND_NAME, M + 24, PAGE_H - 19, { size: 9, bold: true, color: rgb(1, 1, 1) });
  // Right side — workspace title is the anchor, section title sits to its left
  // with a thin separator. Both right-aligned so the visual rail is consistent
  // regardless of section/workspace title length. No more fixed-offset overlap.
  const baselineY = PAGE_H - 19;
  const rightEdge = PAGE_W - M;
  const wsTitle = sanitizeText(info.workspaceTitle || "VA Claim");
  const wsW = canvas.font.widthOfTextAtSize(wsTitle, 8);
  canvas.text(wsTitle, rightEdge - wsW, baselineY, { size: 8, color: rgb(1, 1, 1) });
  const sectionLabel = canvas.section || "Claim Package";
  const sectionW = canvas.font.widthOfTextAtSize(sectionLabel, 8);
  const sepX = rightEdge - wsW - 10;
  // 8pt vertical separator between section and workspace.
  page.drawLine({
    start: { x: sepX, y: baselineY + 2 },
    end:   { x: sepX, y: baselineY + 9 },
    thickness: 0.6,
    color: rgb(0.55, 0.62, 0.78),
  });
  canvas.text(sectionLabel, sepX - 6 - sectionW, baselineY, { size: 8, color: rgb(0.78, 0.83, 0.92) });
}

function drawWatermark(canvas, isFree = false) {
  const { page } = canvas;
  if (isFree) {
    // Bigger, redder watermark for free plan to drive upgrades
    page.drawText("FREE - UPGRADE TO REMOVE", {
      x: 30, y: 380, size: 44,
      font: canvas.bold, color: rgb(0.92, 0.62, 0.62),
      rotate: degrees(34), opacity: 0.55,
    });
    page.drawText("PLANNING DOCUMENT ONLY", {
      x: 90, y: 250, size: 36,
      font: canvas.bold, color: rgb(0.88, 0.90, 0.94),
      rotate: degrees(34), opacity: 0.55,
    });
  } else {
    page.drawText("PLANNING DOCUMENT ONLY", {
      x: 90, y: 380, size: 56,
      font: canvas.bold, color: rgb(0.88, 0.90, 0.94),
      rotate: degrees(34), opacity: 0.55,
    });
  }
}

function drawFooter(canvas, pageNum, totalPages, info) {
  const { page } = canvas;
  page.drawLine({ start: { x: M, y: 50 }, end: { x: PAGE_W - M, y: 50 }, thickness: 0.6, color: C_BORDER });
  canvas.text(
    `${BRAND_NAME} - Unofficial planning document. NOT an official VA form. See disclaimer.`,
    M, 36, { size: 7, color: C_MUTED },
  );
  const pageStr = `Page ${pageNum} of ${totalPages}`;
  const w = canvas.font.widthOfTextAtSize(pageStr, 7);
  canvas.text(pageStr, PAGE_W - M - w, 36, { size: 7, color: C_MUTED, bold: true });
  canvas.text(info.generatedAt, M, 24, { size: 7, color: C_MUTED });
}

// ---- Page builders ----

// ---- Section helpers ----

// Suggested evidence types per pillar — used by the Missing Evidence
// Action Plan + Evidence Index "Why this matters" callouts.
const PILLAR_GUIDANCE = {
  current: {
    why: "Proves the condition exists TODAY. Without a current diagnosis or treatment record, the VA cannot rate it — full stop.",
    suggestions: [
      "VA primary-care note or specialist consult from the last 12 months",
      "Private provider treatment record with diagnosis",
      "Imaging or lab report (X-ray, MRI, audiogram, PFT, etc.)",
      "Active prescription list naming the medication for this condition",
    ],
  },
  service: {
    why: "Proves something happened during active service. Anchors the claim to a date, place, duty, or exposure on your record.",
    suggestions: [
      "Service Treatment Record (STR) page documenting the injury/illness",
      "Sick-call slip, line-of-duty determination, or incident report",
      "Deployment / mobilization orders + DD-214 narrative",
      "Buddy / lay statement (VA Form 21-10210) corroborating the event",
      "Unit awards, medical evac records, or hazard exposure logs",
    ],
  },
  nexus: {
    why: "Links today's condition to the in-service event. Hardest piece of evidence to obtain — without it, direct service connection usually fails.",
    suggestions: [
      "Medical opinion letter (\"at least as likely as not\") from a qualified provider",
      "Independent Medical Examination (IME) / DBQ from a private specialist",
      "Continuity of symptoms documented from discharge to present",
      "Presumptive pathway (PACT Act, Agent Orange, Gulf War, radiation) — verify eligibility",
      "Secondary linkage opinion (caused or aggravated by an already-service-connected condition)",
    ],
  },
  severity: {
    why: "Drives the percentage rating. The VA pays based on functional impact — frequency, intensity, work and daily-living impairment.",
    suggestions: [
      "Symptom log / flare diary (use the ClaimOps Symptom Tracker)",
      "Personal statement (VA Form 21-10210) describing impact in your own words",
      "Lay / witness statement from family, coworker, or supervisor",
      "Treatment frequency record (ER visits, missed work, therapy attendance)",
      "Range-of-motion notes, audiogram thresholds, GAF/PHQ-9 scores, or DBQ severity sections",
    ],
  },
};

// Triage a condition into Strongest / Needs Evidence / High Risk based on
// pillar coverage. Strongest = all 4 covered (or waived). High Risk = 0 or 1
// covered. Otherwise Needs Evidence.
function triageIssue(issue, evidence, waiverCtx) {
  const covered = evidenceElements.filter(
    (el) => computePillarStatus(issue, el.id, evidence, waiverCtx) === "have",
  ).length;
  if (covered === evidenceElements.length) return "strongest";
  if (covered <= 1) return "risk";
  return "needs";
}

// Compute up to 5 ranked priority actions. Higher impact first.
function computeTopPriorities(info) {
  const actions = [];
  const issues = info.issues || [];
  const gaps = info.gaps || [];

  // 1. Conditions with zero pillar coverage = highest priority
  const zeroCoverage = issues.filter((iss) => triageIssue(iss, info.evidence, info.waiverCtx) === "risk");
  zeroCoverage.slice(0, 2).forEach((iss) => {
    actions.push({
      impact: "CRITICAL",
      headline: `${iss.claimedAs} has almost no evidence attached`,
      detail: "Open this condition in the Claim Tracker and attach at least one document under each of the 4 proof pillars (Current, In-service, Nexus, Severity). Until then, this claim is unlikely to be granted.",
    });
  });

  // 2. Group remaining gaps by pillar to surface systemic weak pillars
  const gapByPillar = new Map();
  gaps.forEach((g) => {
    if (!gapByPillar.has(g.element)) gapByPillar.set(g.element, []);
    gapByPillar.get(g.element).push(g);
  });
  // Order pillars by hardest-to-obtain first (nexus -> service -> severity -> current)
  ["nexus", "service", "severity", "current"].forEach((pid) => {
    const list = gapByPillar.get(pid);
    if (!list || list.length === 0) return;
    const el = evidenceElements.find((e) => e.id === pid);
    const names = list.map((g) => g.issueName).slice(0, 3).join(", ");
    const more = list.length > 3 ? ` (+${list.length - 3} more)` : "";
    actions.push({
      impact: list.length >= 3 ? "HIGH" : "MEDIUM",
      headline: `Close the ${el.label} gap on ${list.length} condition${list.length === 1 ? "" : "s"}`,
      detail: `Affected: ${names}${more}. ${PILLAR_GUIDANCE[pid]?.suggestions[0] || "Attach at least one supporting document."}`,
    });
  });

  // 3. Missing veteran identity blocks the cover sheet and 526EZ
  if (!info.veteranName || info.veteranName === "Veteran" || !info.email) {
    actions.push({
      impact: "MEDIUM",
      headline: "Complete veteran identity on the Master 526EZ form",
      detail: "Open Master Forms and add the veteran's legal name, contact email, and mailing address so the package is filing-ready.",
    });
  }

  // 4. Missing ratings prevent VA math
  const missingRating = issues.filter((i) => !Number(i.estimatedRating));
  if (missingRating.length > 0) {
    actions.push({
      impact: "MEDIUM",
      headline: `Set an estimated rating on ${missingRating.length} condition${missingRating.length === 1 ? "" : "s"}`,
      detail: `Without estimated ratings, the VA Math estimate and monthly compensation projection are incomplete. Conditions affected: ${missingRating.map((i) => i.claimedAs).slice(0, 3).join(", ")}${missingRating.length > 3 ? "..." : ""}.`,
    });
  }

  // 5. No statements drafted yet
  if ((info.statements || []).length === 0 && issues.length > 0) {
    actions.push({
      impact: "MEDIUM",
      headline: "Draft at least one supporting statement",
      detail: "Personal Statement (21-10210/21-4138), Lay/Witness Statement, or Nexus Statement. The Statement Builder will guide you through prompts and produce a draft you can refine.",
    });
  }

  // 6. Everything looks complete
  if (actions.length === 0) {
    actions.push({
      impact: "LOW",
      headline: "Your package looks ready — do a final review pass",
      detail: "Re-read each draft statement, double-check every diagnostic code against eCFR.gov, and confirm with a VSO or accredited representative before filing.",
    });
  }

  return actions.slice(0, 5);
}

function buildCoverPage(canvas, info) {
  canvas.newPage("Cover");
  const { page } = canvas;
  // Full bleed top
  page.drawRectangle({ x: 0, y: PAGE_H - 280, width: PAGE_W, height: 280, color: C_BRAND_DARK });
  page.drawRectangle({ x: 0, y: PAGE_H - 284, width: PAGE_W, height: 4, color: C_BRAND });
  // Big "BN" mark
  page.drawRectangle({ x: M, y: PAGE_H - 130, width: 64, height: 64, color: C_BRAND });
  canvas.text("BN", M + 14, PAGE_H - 110, { size: 28, bold: true, color: rgb(1, 1, 1) });
  canvas.text(BRAND_NAME, M + 80, PAGE_H - 86, { size: 22, bold: true, color: rgb(1, 1, 1) });
  canvas.text(BRAND_TAGLINE, M + 80, PAGE_H - 108, { size: 11, color: rgb(0.75, 0.82, 0.95) });
  canvas.text("CLAIM PLANNING PACKAGE", M, PAGE_H - 180, { size: 12, bold: true, color: rgb(0.62, 0.74, 0.95) });
  canvas.text(info.workspaceTitle || "VA Claim Workspace", M, PAGE_H - 220, { size: 30, bold: true, color: rgb(1, 1, 1) });
  canvas.text(info.claimType || "", M, PAGE_H - 248, { size: 12, color: rgb(0.78, 0.85, 0.96) });

  // Veteran block
  let y = PAGE_H - 330;
  canvas.text("PREPARED FOR", M, y, { size: 9, bold: true, color: C_BRAND }); y -= 16;
  canvas.text(info.veteranName || "Veteran", M, y, { size: 16, bold: true }); y -= 22;
  if (info.email) { canvas.text(info.email, M, y, { size: 10, color: C_MUTED }); y -= 14; }

  // KPI row
  y = PAGE_H - 460;
  const cellW = (PAGE_W - M * 2 - 24) / 3;
  const cells = [
    { label: "EVIDENCE READINESS", value: `${info.readiness}%` },
    { label: "VA MATH ESTIMATE", value: `${info.combinedRating}%` },
    { label: "EST. MONTHLY", value: money(info.estMonthly) },
  ];
  cells.forEach((c, i) => {
    const x = M + i * (cellW + 12);
    page.drawRectangle({ x, y: y - 70, width: cellW, height: 70, color: C_BG_SOFT, borderColor: C_BORDER, borderWidth: 0.7 });
    canvas.text(c.label, x + 14, y - 24, { size: 8, bold: true, color: C_MUTED });
    canvas.text(c.value, x + 14, y - 56, { size: 22, bold: true, color: C_BRAND_DARK });
  });

  // Footer signature line
  y = 220;
  canvas.text("PACKAGE CONTENTS", M, y, { size: 9, bold: true, color: C_BRAND }); y -= 14;
  const toc = [
    "1.  Cover Page",
    "2.  Executive Summary",
    "3.  Top 5 Priority Actions",
    "4.  Quick Readiness Checklist",
    "5.  Condition Readiness Summary",
    "6.  Condition Matrix",
    "7.  VA Math Estimate",
    "8.  Evidence Index by Condition",
    "9.  Missing Evidence Action Plan",
    "10. Statement Packet",
    "11. C&P Exam Prep Sheets",
    "12. Pre-Filing Checklist",
    "13. Post-Filing Checklist",
    "14. Final Reminder",
    "15. Full Disclaimer",
  ];
  toc.forEach((t) => { canvas.text(t, M + 6, y, { size: 9.5, color: C_TEXT }); y -= 12; });

  // Brief disclaimer at the bottom of the cover (full version at end of package)
  canvas.text(
    "PLANNING DOCUMENT ONLY - NOT AN OFFICIAL VA FORM. ClaimOps VA is not a law firm or VSO. See the full disclaimer on the final page.",
    M, 130, { size: 8, color: C_MUTED, bold: true },
  );
  canvas.text(`Generated: ${info.generatedAt}`, M, 110, { size: 9, color: C_MUTED });
  canvas.text(BRAND_NAME, PAGE_W - M - canvas.bold.widthOfTextAtSize(BRAND_NAME, 9), 110, { size: 9, bold: true, color: C_BRAND });
}

function buildExecutiveSummary(canvas, info) {
  canvas.newPage("Executive Summary");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Executive Summary");
  canvas.para(
    `Snapshot of your claim package as of ${info.generatedAt}. Use this page to orient yourself before reviewing the detail sections.`,
    { size: 10 },
  );
  canvas.y -= 6;

  // --- Plain-language explainer: Combined Rating vs Package Readiness ---
  const explX = M;
  const explW = PAGE_W - M * 2;
  const explH = 78;
  canvas.ensure(explH + 10);
  canvas.rect(explX, canvas.y - explH, explW, explH, { fill: C_BG_SOFT, stroke: C_BORDER });
  canvas.text("TWO NUMBERS TO UNDERSTAND", explX + 12, canvas.y - 16, { size: 8, bold: true, color: C_BRAND });
  canvas.text("Estimated Combined Rating", explX + 12, canvas.y - 32, { size: 10, bold: true, color: C_BRAND_DARK });
  canvas.text(
    "Math-only projection of what your conditions would combine to under 38 CFR 4.25 IF every rating were granted as estimated.",
    explX + 12, canvas.y - 46, { size: 8.5, color: C_TEXT, maxWidth: explW - 24 },
  );
  canvas.text("Package Readiness", explX + 12, canvas.y - 60, { size: 10, bold: true, color: C_BRAND_DARK });
  canvas.text(
    "Measures whether each claimed condition has evidence for Current Disability, In-Service Event, Nexus/Link, and Severity/Function.",
    explX + 12, canvas.y - 74, { size: 8.5, color: C_TEXT, maxWidth: explW - 24 },
  );
  canvas.y -= explH + 8;

  // --- KPI grid ---
  const items = [
    { label: "Claimed Conditions", value: `${info.issues.length}` },
    { label: "Evidence Items Logged", value: `${info.evidence.length}` },
    { label: "Pillars Covered", value: `${info.pillarsCovered} of ${info.pillarsTotal}` },
    { label: "Pillars Still Needed", value: `${info.criticalGaps}` },
    { label: "Package Readiness", value: `${info.readiness}%` },
    { label: "Est. Combined Rating", value: `${info.combinedRating}%` },
    { label: "Bilateral Factor", value: info.bilateralApplied ? "Applied" : "N/A" },
    { label: "Est. Monthly", value: money(info.estMonthly) },
  ];
  canvas.ensure(140);
  const cols = 4;
  const cellW = (PAGE_W - M * 2 - (cols - 1) * 10) / cols;
  const cellH = 40;
  let startY = canvas.y;
  items.forEach((it, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = M + col * (cellW + 10);
    const y = startY - row * (cellH + 8);
    canvas.rect(x, y - cellH, cellW, cellH, { fill: C_BG_SOFT, stroke: C_BORDER });
    canvas.text(it.label.toUpperCase(), x + 8, y - 13, { size: 6.5, bold: true, color: C_MUTED, maxWidth: cellW - 12 });
    canvas.text(it.value, x + 8, y - 30, { size: 13, bold: true, color: C_BRAND_DARK });
  });
  canvas.y = startY - Math.ceil(items.length / cols) * (cellH + 8) - 10;

  // --- Bottom Line box ---
  canvas.ensure(120);
  const blY = canvas.y;
  const blH = 100;
  canvas.rect(M, blY - blH, PAGE_W - M * 2, blH, { fill: rgb(0.95, 0.97, 1.0), stroke: C_BRAND });
  // Left accent bar
  canvas.rect(M, blY - blH, 4, blH, { fill: C_BRAND });
  canvas.text("BOTTOM LINE", M + 16, blY - 18, { size: 9, bold: true, color: C_BRAND });
  const bottomLine = computeBottomLine(info);
  canvas.text(bottomLine.headline, M + 16, blY - 36, { size: 12, bold: true, color: C_BRAND_DARK, maxWidth: PAGE_W - M * 2 - 32 });
  const detailLines = wrap(bottomLine.detail, 94);
  let by = blY - 52;
  detailLines.forEach((ln) => { canvas.text(ln, M + 16, by, { size: 9, color: C_TEXT }); by -= 12; });
  canvas.y = blY - blH - 8;
}

function computeBottomLine(info) {
  const r = info.readiness;
  const combined = info.combinedRating;
  if (info.issues.length === 0) {
    return {
      headline: "Add your first condition to get started.",
      detail: "Open the Claim Tracker, click ADD CONDITION, pick a diagnostic code, and start logging evidence under each of the four proof pillars.",
    };
  }
  if (r >= 75) {
    return {
      headline: `You're filing-ready. Estimated combined rating: ${combined}%.`,
      detail: "Every claimed condition has solid evidence across all four pillars. Do a final review of statements, confirm form revisions on VA.gov/find-forms, and consider running this past a VSO or accredited representative before submitting.",
    };
  }
  if (r >= 40) {
    return {
      headline: `Almost there. Readiness ${r}% · Est. rating ${combined}%.`,
      detail: `You have ${info.criticalGaps} proof pillar${info.criticalGaps === 1 ? "" : "s"} still missing evidence. Focus on the Top 5 Priority Actions on the next page — closing those gaps is the fastest path to a stronger claim.`,
    };
  }
  return {
    headline: `Early draft. Readiness ${r}% · Est. rating ${combined}%.`,
    detail: `Your math may already total ${combined}%, but ${info.criticalGaps} proof pillar${info.criticalGaps === 1 ? "" : "s"} still need evidence. The VA decides claims on the evidence in the file, not the projected rating. Work the Top 5 Priority Actions list and re-export this package when you've closed those gaps.`,
  };
}

function buildTopPriorities(canvas, info) {
  canvas.newPage("Top 5 Priority Actions");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Top 5 Priority Actions");
  canvas.para(
    "Ranked by impact on claim strength. Work these in order — closing higher-impact items first delivers the biggest readiness gain per hour of effort.",
    { size: 10 },
  );
  canvas.y -= 6;

  const priorities = computeTopPriorities(info);
  const impactColor = { CRITICAL: C_RED, HIGH: C_AMBER, MEDIUM: C_BLUE, LOW: C_GREEN };
  const impactBg = {
    CRITICAL: rgb(0.99, 0.94, 0.94),
    HIGH: rgb(0.99, 0.97, 0.91),
    MEDIUM: rgb(0.93, 0.96, 1.0),
    LOW: rgb(0.92, 0.99, 0.95),
  };

  priorities.forEach((p, idx) => {
    canvas.ensure(80);
    const startY = canvas.y;
    const boxH = 70;
    // Outer card
    canvas.rect(M, startY - boxH, PAGE_W - M * 2, boxH, { fill: rgb(1, 1, 1), stroke: C_BORDER });
    // Number badge
    canvas.rect(M, startY - boxH, 36, boxH, { fill: C_BRAND_DARK });
    canvas.text(`${idx + 1}`, M + 10, startY - 28, { size: 22, bold: true, color: rgb(1, 1, 1) });
    canvas.text("ACTION", M + 4, startY - 56, { size: 6.5, bold: true, color: rgb(0.78, 0.83, 0.92) });
    // Impact pill
    const impact = p.impact;
    const pillW = canvas.bold.widthOfTextAtSize(impact, 7.5) + 12;
    canvas.rect(M + 48, startY - 22, pillW, 13, { fill: impactBg[impact], stroke: impactColor[impact] });
    canvas.text(impact, M + 54, startY - 19, { size: 7.5, bold: true, color: impactColor[impact] });
    // Headline + detail
    canvas.text(p.headline, M + 48, startY - 38, { size: 11, bold: true, color: C_BRAND_DARK, maxWidth: PAGE_W - M * 2 - 56 });
    const detailLines = wrap(p.detail, 90);
    let dy = startY - 52;
    detailLines.slice(0, 2).forEach((ln) => {
      canvas.text(ln, M + 48, dy, { size: 8.5, color: C_TEXT, maxWidth: PAGE_W - M * 2 - 56 });
      dy -= 11;
    });
    canvas.y = startY - boxH - 8;
  });

  if (priorities.length < 5) {
    canvas.y -= 4;
    canvas.text(`(${5 - priorities.length} additional slot${priorities.length === 4 ? "" : "s"} unused - your package is in good shape.)`, M, canvas.y, { size: 8.5, color: C_MUTED });
  }
}

function buildConditionReadinessSummary(canvas, info) {
  canvas.newPage("Condition Readiness Summary");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Condition Readiness Summary");
  canvas.para(
    "Every claimed condition is triaged into one of three buckets based on how many of the four proof pillars are covered. Use this to decide where to spend your next hour.",
    { size: 10 },
  );
  canvas.y -= 6;

  if (info.issues.length === 0) {
    canvas.para("No conditions claimed yet. Add one in the Claim Tracker to populate this summary.", { color: C_MUTED });
    return;
  }

  const buckets = { strongest: [], needs: [], risk: [] };
  info.issues.forEach((iss) => {
    const tier = triageIssue(iss, info.evidence, info.waiverCtx);
    buckets[tier].push(iss);
  });

  const renderBucket = (title, items, accent, bg, helpText) => {
    canvas.ensure(40);
    const headerY = canvas.y;
    canvas.rect(M, headerY - 16, PAGE_W - M * 2, 18, { fill: bg, stroke: accent });
    canvas.text(`${title}  (${items.length})`, M + 10, headerY - 12, { size: 10, bold: true, color: accent });
    canvas.y = headerY - 22;
    canvas.text(helpText, M + 10, canvas.y, { size: 8, color: C_MUTED });
    canvas.y -= 14;
    if (items.length === 0) {
      canvas.text("(none)", M + 14, canvas.y, { size: 9, color: C_MUTED });
      canvas.y -= 14;
      return;
    }
    items.forEach((iss) => {
      const covered = evidenceElements
        .filter((el) => computePillarStatus(iss, el.id, info.evidence, info.waiverCtx) === "have")
        .map((el) => el.label);
      const missing = evidenceElements
        .filter((el) => computePillarStatus(iss, el.id, info.evidence, info.waiverCtx) !== "have")
        .map((el) => el.label);
      canvas.ensure(28);
      canvas.text(`${iss.claimedAs}  (DC ${iss.code})`, M + 14, canvas.y, { size: 10, bold: true, color: C_BRAND_DARK, maxWidth: PAGE_W - M * 2 - 16 });
      canvas.y -= 12;
      if (covered.length > 0) {
        canvas.text(`Have: ${covered.join(", ")}`, M + 22, canvas.y, { size: 8.5, color: C_GREEN, maxWidth: PAGE_W - M * 2 - 24 });
        canvas.y -= 10;
      }
      if (missing.length > 0) {
        canvas.text(`Need: ${missing.join(", ")}`, M + 22, canvas.y, { size: 8.5, color: C_RED, maxWidth: PAGE_W - M * 2 - 24 });
        canvas.y -= 10;
      }
      canvas.y -= 4;
    });
    canvas.y -= 4;
  };

  renderBucket(
    "STRONGEST CONDITIONS",
    buckets.strongest,
    C_GREEN,
    rgb(0.92, 0.99, 0.95),
    "All 4 proof pillars covered. Review statements + C&P prep and file with confidence.",
  );
  renderBucket(
    "NEEDS EVIDENCE",
    buckets.needs,
    C_AMBER,
    rgb(0.99, 0.97, 0.91),
    "2-3 pillars covered. Close the remaining gaps before filing for the best chance of approval.",
  );
  renderBucket(
    "HIGH RISK / NOT READY",
    buckets.risk,
    C_RED,
    rgb(0.99, 0.94, 0.94),
    "0-1 pillars covered. These claims are unlikely to be granted in their current state. Prioritise them or consider deferring.",
  );
}

function buildConditionMatrix(canvas, info) {
  canvas.newPage("Condition Matrix");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Condition Matrix");
  canvas.para(
    "Each row summarizes one claimed condition, its diagnostic code, theory of service connection, estimated rating, " +
    "and the auto-computed status of each of the four proof pillars.",
  );
  canvas.y -= 4;

  // Table header
  // Column widths must sum to <= PAGE_W - M*2 (= 516pt) or the table overflows
  // the right margin and the row divider rules visibly stop short of the data.
  // Total below: 130 + 44 + 78 + 36 + 56 + 56 + 56 + 56 = 512pt.
  const cols = [
    { label: "Condition", width: 130 },
    { label: "DC", width: 44 },
    { label: "Theory", width: 78 },
    { label: "Est %", width: 36 },
    { label: "Current", width: 56 },
    { label: "Service", width: 56 },
    { label: "Nexus", width: 56 },
    { label: "Severity", width: 56 },
  ];
  const drawRow = (cells, opts = {}) => {
    const { header = false, status = null, bg = null } = opts;
    canvas.ensure(20);
    let x = M;
    // The drawable area ends at PAGE_W - M. Clip the table width to that bound
    // so background fills + dividers + columns all share the same right edge.
    const tableRight = PAGE_W - M;
    if (bg) canvas.rect(M, canvas.y - 4, tableRight - M, 18, { fill: bg });
    cols.forEach((c, i) => {
      const text = cells[i] ?? "";
      if (header) {
        canvas.text(text, x + 4, canvas.y + 2, { size: 8, bold: true, color: C_MUTED });
      } else if (status && i >= 4) {
        const s = status[i - 4];
        canvas.statusPill(s.toUpperCase(), s, x + 4, canvas.y + 2);
      } else {
        canvas.text(text, x + 4, canvas.y + 2, { size: 8, color: C_TEXT, maxWidth: c.width - 8 });
      }
      x += c.width;
    });
    canvas.y -= 14;
    canvas.drawLine(M, canvas.y + 2, tableRight, canvas.y + 2, C_BORDER, 0.4);
    canvas.y -= 4;
  };
  drawRow(cols.map((c) => c.label), { header: true });

  info.issues.forEach((issue) => {
    const statuses = evidenceElements.map((el) => computePillarStatus(issue, el.id, info.evidence, info.waiverCtx));
    drawRow(
      [issue.claimedAs, issue.code, issue.theory, `${issue.estimatedRating || 0}%`, "", "", "", ""],
      { status: statuses },
    );
  });

  if (info.issues.length === 0) canvas.para("No conditions claimed yet.", { color: C_MUTED });

  // Summary
  canvas.y -= 10;
  canvas.sectionTitle("VA Math Combined");
  canvas.bullet(`Raw combined: ${info.rawCombined}% (rounded to ${info.combinedRating}%).`);
  if (info.bilateralApplied) {
    canvas.bullet(`Bilateral factor applied: base ${info.bilateralBase}% + 10% factor = ${info.bilateralCombinedWithFactor}% bilateral subtotal.`);
  } else {
    canvas.bullet("Bilateral factor: not applicable (requires paired left AND right ratings on same extremity group).");
  }
}

function buildEvidenceIndex(canvas, info) {
  canvas.newPage("Evidence Index");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Evidence Index by Condition");
  canvas.para(
    "All logged evidence, grouped by claimed condition and then by proof pillar. Each pillar header includes a \"Why this matters\" line so you (and your VSO or rep) can see at a glance what the evidence is doing for the claim.",
  );
  canvas.y -= 4;

  info.issues.forEach((issue) => {
    canvas.ensure(36);
    canvas.sectionTitle(`${issue.claimedAs}  (DC ${issue.code})`);
    canvas.text(`Theory: ${issue.theory} | System: ${bodySystems.find((s) => s.id === issue.system)?.label || issue.system}`, M, canvas.y, { size: 8, color: C_MUTED });
    canvas.y -= 14;

    const issueEv = info.evidence.filter((ev) => ev.issueIds?.includes(issue.id));
    if (issueEv.length === 0) {
      canvas.para("No evidence logged for this condition yet — see the Missing Evidence Action Plan for suggested next steps.", { color: C_MUTED, size: 9, indent: 6 });
      canvas.y -= 4;
      return;
    }
    evidenceElements.forEach((el) => {
      const items = issueEv.filter((ev) => ev.element === el.id);
      if (items.length === 0) return;
      canvas.ensure(34);
      // Pillar header with Why-this-matters callout
      canvas.text(el.label.toUpperCase(), M + 6, canvas.y, { size: 8, bold: true, color: C_BRAND });
      canvas.y -= 11;
      const why = PILLAR_GUIDANCE[el.id]?.why || el.help;
      wrap(`Why this matters: ${why}`, 100).forEach((line) => {
        canvas.ensure(10);
        canvas.text(line, M + 6, canvas.y, { size: 7.5, color: C_MUTED });
        canvas.y -= 9;
      });
      canvas.y -= 2;
      items.forEach((ev) => {
        canvas.ensure(24);
        canvas.text("\u2022", M + 14, canvas.y, { size: 9, bold: true, color: C_BRAND });
        canvas.text(ev.title || "Untitled", M + 26, canvas.y, { size: 9, bold: true, maxWidth: 380 });
        if (ev.date) canvas.text(ev.date, PAGE_W - M - 60, canvas.y, { size: 8, color: C_MUTED });
        canvas.y -= 12;
        if (ev.source || ev.fileName) {
          canvas.text(`${ev.source || ""}${ev.source && ev.fileName ? " - " : ""}${ev.fileName || ""}`, M + 26, canvas.y, { size: 8, color: C_MUTED, maxWidth: 460 });
          canvas.y -= 10;
        }
        // Evidence purpose: prefer per-item override, fall back to pillar-level
        const purpose = ev.purpose || ev.whyItMatters || "";
        if (purpose) {
          wrap(`Purpose: ${purpose}`, 95).slice(0, 2).forEach((line) => {
            canvas.ensure(10);
            canvas.text(line, M + 26, canvas.y, { size: 8, color: C_BLUE });
            canvas.y -= 10;
          });
        }
        if (ev.summary) {
          wrap(ev.summary, 95).slice(0, 3).forEach((line) => {
            canvas.ensure(10);
            canvas.text(line, M + 26, canvas.y, { size: 8, color: C_TEXT });
            canvas.y -= 10;
          });
        }
        canvas.y -= 4;
      });
    });
    canvas.y -= 6;
  });
}

function buildMissingPlan(canvas, info) {
  canvas.newPage("Missing Evidence Action Plan");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Missing Evidence Action Plan");
  canvas.para(
    "Grouped by proof-pillar category first, then by affected condition. Each section lists the specific evidence types most likely to close that gap. Work the pillars from hardest to easiest: Nexus, In-Service, Severity, Current.",
  );
  canvas.y -= 4;

  if (info.gaps.length === 0) {
    canvas.para("No gaps detected. Every claimed condition has evidence attached for all four proof pillars. Move on to the Statement Packet and C&P Exam Prep sections.", { color: C_GREEN, bold: true });
    return;
  }

  // Group by element (pillar) first, then by condition
  const byElement = new Map();
  info.gaps.forEach((g) => {
    if (!byElement.has(g.element)) byElement.set(g.element, []);
    byElement.get(g.element).push(g);
  });

  // Render order matches the Top 5 logic — hardest-to-obtain pillars first
  const order = ["nexus", "service", "severity", "current"];
  order.forEach((pid) => {
    const gapsForPillar = byElement.get(pid);
    if (!gapsForPillar || gapsForPillar.length === 0) return;
    const el = evidenceElements.find((e) => e.id === pid);
    const guidance = PILLAR_GUIDANCE[pid] || { why: el.help, suggestions: [] };

    canvas.ensure(50);
    // Pillar banner
    const banY = canvas.y;
    canvas.rect(M, banY - 22, PAGE_W - M * 2, 24, { fill: rgb(0.97, 0.94, 0.94), stroke: C_RED });
    canvas.text(`MISSING ${el.label.toUpperCase()} EVIDENCE`, M + 12, banY - 16, { size: 10, bold: true, color: C_RED });
    canvas.text(`${gapsForPillar.length} condition${gapsForPillar.length === 1 ? "" : "s"} affected`, PAGE_W - M - 130, banY - 16, { size: 8, color: C_MUTED });
    canvas.y = banY - 30;

    // Why this matters
    wrap(`Why this matters: ${guidance.why}`, 100).forEach((line) => {
      canvas.ensure(11);
      canvas.text(line, M + 6, canvas.y, { size: 9, color: C_TEXT });
      canvas.y -= 11;
    });
    canvas.y -= 4;

    // Suggested evidence types
    canvas.text("SUGGESTED EVIDENCE TYPES", M + 6, canvas.y, { size: 8, bold: true, color: C_BRAND });
    canvas.y -= 12;
    guidance.suggestions.forEach((s) => {
      canvas.bullet(s, { size: 9 });
    });
    canvas.y -= 4;

    // Affected conditions
    canvas.text("AFFECTED CONDITIONS", M + 6, canvas.y, { size: 8, bold: true, color: C_BRAND });
    canvas.y -= 12;
    gapsForPillar.forEach((g) => {
      canvas.ensure(14);
      canvas.text(`- ${g.issueName}`, M + 14, canvas.y, { size: 9, color: C_TEXT, maxWidth: PAGE_W - M * 2 - 16 });
      canvas.y -= 11;
    });
    canvas.y -= 8;
  });
}

function buildStatementPacket(canvas, info) {
  if (!info.statements.length) return;
  canvas.newPage("Statement Packet");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Statement Packet");
  canvas.para(
    "Drafts assembled from your guided prompts. Personal and lay/witness statements typically submit on VA Form 21-10210 or 21-4138. A nexus statement is a medical-opinion letter and must be signed by a qualified medical provider.",
  );
  canvas.y -= 6;

  // Warning callout
  const warnH = 38;
  canvas.ensure(warnH + 8);
  const warnY = canvas.y;
  canvas.rect(M, warnY - warnH, PAGE_W - M * 2, warnH, { fill: rgb(0.99, 0.94, 0.94), stroke: C_RED });
  canvas.text("REVIEW BEFORE SUBMISSION", M + 12, warnY - 14, { size: 8, bold: true, color: C_RED });
  canvas.text(
    "These are DRAFTS, not final submissions. Read every statement aloud, edit for accuracy, sign and date, and consider having a VSO or rep review before filing.",
    M + 12, warnY - 28, { size: 8.5, color: C_TEXT, maxWidth: PAGE_W - M * 2 - 24 },
  );
  canvas.y = warnY - warnH - 10;

  // Overview table
  canvas.sectionTitle("Statement Overview");
  const colW = [200, 90, 160, 60]; // Condition, Type, Purpose, Status — total 510
  const headers = ["Condition / Title", "Type", "Purpose", "Status"];
  canvas.ensure(18);
  let hx = M;
  headers.forEach((h, i) => {
    canvas.text(h.toUpperCase(), hx + 4, canvas.y, { size: 7, bold: true, color: C_MUTED });
    hx += colW[i];
  });
  canvas.y -= 11;
  canvas.drawLine(M, canvas.y + 2, PAGE_W - M, canvas.y + 2, C_BORDER, 0.6);
  canvas.y -= 4;
  info.statements.forEach((stmt) => {
    canvas.ensure(14);
    const status = (stmt.answers && Object.keys(stmt.answers).length > 0) ? "Drafted" : "Empty";
    const purpose = statementPurpose(stmt.type);
    const cells = [stmt.title || "Untitled", stmt.type || "—", purpose, status];
    let x = M;
    cells.forEach((c, i) => {
      const color = (i === 3 && status === "Empty") ? C_MUTED : (i === 3 ? C_GREEN : C_TEXT);
      canvas.text(c, x + 4, canvas.y, { size: 8.5, color, maxWidth: colW[i] - 8 });
      x += colW[i];
    });
    canvas.y -= 12;
    canvas.drawLine(M, canvas.y + 2, PAGE_W - M, canvas.y + 2, C_BORDER, 0.3);
    canvas.y -= 4;
  });
  canvas.y -= 8;

  // Full drafts
  info.statements.forEach((stmt, idx) => {
    canvas.ensure(40);
    canvas.sectionTitle(`Statement ${idx + 1} - ${stmt.title || stmt.type}`);
    canvas.text(`Type: ${stmt.type}`, M, canvas.y, { size: 9, color: C_MUTED, bold: true });
    canvas.y -= 14;
    const body = buildStatementText(stmt.type, stmt.answers || {});
    if (!body) {
      canvas.para("(No content drafted yet for this statement.)", { color: C_MUTED });
      return;
    }
    canvas.para(body, { size: 9, width: 95 });
    canvas.y -= 10;
  });
}

function statementPurpose(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("nexus")) return "Medical-opinion link";
  if (t.includes("lay") || t.includes("witness")) return "Third-party corroboration";
  if (t.includes("personal")) return "Veteran's first-person account";
  if (t.includes("ptsd") || t.includes("mst")) return "Stressor / trauma statement";
  return "Supporting evidence";
}

function buildCpPrep(canvas, info) {
  if (info.issues.length === 0) return;
  canvas.newPage("C&P Exam Prep");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("C&P Exam Prep Sheets");
  canvas.para(
    "These sheets are FACTUAL MEMORY AIDS — not scripts. Bring them to your Compensation & Pension examination to keep your timeline, symptoms, and treatments straight. Do NOT read from them verbatim or rehearse answers. Examiners are trained to spot coached responses, and inconsistencies between what you say and what's in your records hurt the claim.",
    { size: 9.5 },
  );
  canvas.para(
    "Be specific, honest, and consistent. \"On a bad day, I can't lift more than a gallon of milk without sharp pain in my lower back\" is more useful than \"my back hurts.\"",
    { size: 9, color: C_MUTED },
  );
  canvas.y -= 4;

  info.issues.forEach((issue) => {
    const res = getConditionResources(issue.code, issue.system);
    canvas.ensure(80);
    canvas.sectionTitle(`${issue.claimedAs}  (DC ${issue.code})`);
    canvas.text(`DBQ: ${res.dbqName}    |    Rating reference: ${res.cfrRef || "38 CFR Part 4"}`, M, canvas.y, { size: 8.5, color: C_MUTED });
    canvas.y -= 14;
    if (res.cpPrep?.length) {
      res.cpPrep.forEach((item) => canvas.bullet(item, { size: 9 }));
    } else {
      canvas.bullet("No condition-specific prep guidance available. Review the applicable DBQ and 38 CFR rating criteria before the exam.");
    }
    // Per-condition functional impact reminder
    if (issue.functionalImpact) {
      canvas.y -= 4;
      canvas.text("YOUR FUNCTIONAL IMPACT NOTES", M + 6, canvas.y, { size: 7.5, bold: true, color: C_BRAND });
      canvas.y -= 11;
      canvas.para(issue.functionalImpact, { size: 9, indent: 6, width: 95 });
    }
    canvas.y -= 8;
  });
}

function buildVaMathSummary(canvas, info) {
  canvas.newPage("VA Math Estimate");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("VA Math Estimate");
  canvas.para(
    "Reminder: this is a math-only projection of what your conditions would combine to IF each were granted as estimated. It is NOT a guarantee. The VA combines ratings progressively per 38 CFR 4.25 — each additional rating only reduces the remaining efficiency, never adds linearly. After combination, the result is rounded to the nearest 10.",
  );
  canvas.y -= 4;

  // Sort ratings high to low, show stepwise combination
  const ratings = info.issues.map((i) => Number(i.estimatedRating || 0)).filter((r) => r > 0).sort((a, b) => b - a);
  canvas.sectionTitle("Stepwise Combination");
  if (!ratings.length) {
    canvas.para("No compensable ratings entered yet.", { color: C_MUTED });
  } else {
    let combined = 0;
    ratings.forEach((r, idx) => {
      const before = combined;
      combined += (100 - combined) * (r / 100);
      const display = `Step ${idx + 1}: ${before.toFixed(1)}% + ${r}%  =>  ${combined.toFixed(2)}%`;
      canvas.bullet(display, { size: 9 });
    });
    const finalRound = Math.min(100, Math.round(combined / 10) * 10);
    canvas.y -= 4;
    canvas.text(`Raw combined: ${combined.toFixed(2)}%`, M, canvas.y, { size: 10, bold: true }); canvas.y -= 14;
    canvas.text(`VA-rounded: ${finalRound}%`, M, canvas.y, { size: 14, bold: true, color: C_BRAND }); canvas.y -= 18;
  }

  canvas.sectionTitle("Bilateral Factor (38 CFR 4.26)");
  if (info.bilateralApplied) {
    canvas.bullet(`Paired extremities qualify. Bilateral subtotal: ${info.bilateralBase}% + 10% factor = ${info.bilateralCombinedWithFactor}%.`);
    canvas.bullet("This bilateral subtotal was combined with all other non-paired ratings before final rounding.");
  } else {
    canvas.bullet("No bilateral factor applied. To qualify, you need rated conditions on BOTH left AND right of the same paired extremity group (e.g., both knees, both shoulders).");
  }

  canvas.y -= 4;
  canvas.sectionTitle("Estimated 2026 Monthly Compensation");
  const va = info.va;
  canvas.bullet(`Rating used: ${va.rating}%`);
  canvas.bullet(`Dependent basis: ${va.basis}`);
  canvas.bullet(`Estimated monthly: ${money(va.monthly)}`);
  canvas.bullet(`Estimated yearly: ${money(va.yearly)}`);

  if (info.retired.monthly > 0) {
    canvas.y -= 4;
    canvas.sectionTitle("High-3 Retirement Estimate");
    canvas.bullet(`Years of service: ${info.retired.years.toFixed(2)}`);
    canvas.bullet(`Multiplier: ${(info.retired.multiplier * 100).toFixed(1)}%`);
    canvas.bullet(`Estimated monthly retired pay: ${money(info.retired.monthly)}`);
  }
}

function computeChecklistItems(info) {
  const issues = info.issues || [];
  const evidence = info.evidence || [];
  const stmts = info.statements || [];
  const hasIssues = issues.length > 0;
  const everyIssue = (fn) => hasIssues && issues.every(fn);
  const anyEvidence = evidence.length > 0;
  const veteranNameOk = !!(info.veteranName && info.veteranName.trim() && info.veteranName.trim() !== "Veteran");
  const emailOk = !!(info.email && /@/.test(info.email));

  // Pillar coverage per element (have evidence attached on every claimed condition)
  const pillarOk = (elId) => hasIssues && issues.every((iss) => {
    const st = computePillarStatus(iss, elId, evidence, info.waiverCtx);
    return st === "have";
  });
  const elById = (id) => evidenceElements.find((e) => e.id === id) || { label: id };

  const stmtType = (t) => stmts.some((s) => (s.type || "").toLowerCase() === t);

  return {
    identity: [
      { ok: veteranNameOk, label: "Veteran legal name entered" },
      { ok: emailOk, label: "Contact email entered" },
      { ok: (info.retired?.years || 0) > 0, label: "Retirement basis (years of service / High-3) entered, if applying for retired pay estimate" },
    ],
    conditions: [
      { ok: hasIssues, label: `At least one claimed condition added (currently: ${issues.length})` },
      { ok: everyIssue((i) => !!i.code), label: "Every condition has a diagnostic code (DC) assigned" },
      { ok: everyIssue((i) => !!i.theory), label: "Every condition has a theory of service connection set (Direct, Secondary, Presumptive, Aggravation)" },
      { ok: everyIssue((i) => Number(i.estimatedRating) > 0), label: "Every condition has an estimated rating set (drives VA Math + financial estimates)" },
      { ok: everyIssue((i) => !!(i.functionalImpact || "").trim()), label: "Every condition has a functional impact statement written" },
    ],
    evidence: [
      { ok: anyEvidence, label: `At least one evidence item logged (currently: ${evidence.length})` },
      ...evidenceElements.map((el) => ({ ok: pillarOk(el.id), label: `${el.label} pillar has evidence attached on every condition` })),
      { ok: info.criticalGaps === 0, label: "No pillars are still marked NEED IT across the package" },
    ],
    statements: [
      { ok: stmtType("personal"), label: "At least one Personal Statement drafted (VA Form 21-10210 or 21-4138)" },
      { ok: stmtType("lay") || stmtType("witness") || stmtType("lay-witness"), label: "At least one Lay / Witness Statement drafted (recommended)" },
      { ok: stmtType("nexus"), label: "At least one Nexus Statement prepared (medical opinion - signed by a qualified provider)" },
    ],
    submission: [
      { ok: false, label: "Confirm the official VA Form 21-526EZ on VA.gov/find-forms is the current revision" },
      { ok: false, label: "If claiming PTSD/MST, include VA Form 21-0781 or 21-0781a" },
      { ok: false, label: "Attach a current copy of DD-214 (member-4 copy) or service record if VA does not already have it" },
      { ok: false, label: "Sign and date every statement and form" },
      { ok: false, label: "Save a complete copy of everything submitted (PDF + paper)" },
      { ok: false, label: "Read the Important Disclaimer at the end of this package" },
    ],
    postFiling: [
      { ok: false, label: "Watch VA.gov claim status for evidence requests (5103 letter) within 30 days" },
      { ok: false, label: "Schedule any VA-requested C&P exams - do NOT miss them" },
      { ok: false, label: "Log every appointment in the ClaimOps Appointments tab and bring the Day-of-Exam Packet" },
      { ok: false, label: "If denied, you generally have 1 year to file a Supplemental Claim, Higher-Level Review, or Notice of Disagreement" },
    ],
  };
}

function buildChecklistSummary(canvas, info) {
  canvas.newPage("Readiness Checklist");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Readiness Checklist (Quick Look)");
  canvas.para(
    "A quick snapshot of high-level items the app could verify automatically. A green check means ClaimOps detected the item is done based on what you have entered. " +
    "Empty boxes are either incomplete or items that only you can confirm. The full checklist appears near the end of this package.",
  );
  canvas.y -= 6;

  const cl = computeChecklistItems(info);
  const summary = [
    cl.identity[0],                                  // name
    cl.conditions[0],                                // at least one condition
    cl.conditions[3],                                // every condition has est rating
    cl.evidence[0],                                  // at least one evidence item
    { ok: info.criticalGaps === 0, label: "All proof pillars have evidence on every condition" },
    { ok: (info.statements || []).length > 0, label: "At least one supporting statement drafted" },
    { ok: false, label: "I have read and understood the disclaimer at the end of this package" },
  ];
  const totalAuto = summary.filter((i) => i.ok !== false || i.ok === true).length;
  const doneAuto = summary.filter((i) => i.ok === true).length;
  canvas.text(`Auto-verified: ${doneAuto} of ${summary.length} items`, M, canvas.y, { size: 9, bold: true, color: C_BRAND });
  canvas.y -= 16;
  void totalAuto;

  summary.forEach((it) => canvas.checkboxItem(!!it.ok, it.label, { size: 10 }));

  canvas.y -= 6;
  canvas.sectionTitle("Tip");
  canvas.para(
    "Use this quick checklist to spot the biggest gaps at a glance. Then jump to the FULL CHECKLIST near the end for the complete pre-filing and post-filing review.",
    { size: 9, color: C_MUTED },
  );
}

function buildPreFilingChecklist(canvas, info) {
  canvas.newPage("Pre-Filing Checklist");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Pre-Filing Checklist");
  canvas.para(
    "Walk through every section before you file. Green checks were auto-detected by ClaimOps from data you entered. Empty boxes are either incomplete or items only you can confirm (signing forms, mailing packets). This list is for organization only — it is not a substitute for guidance from a VSO, accredited agent, or attorney.",
    { size: 9 },
  );
  canvas.y -= 4;

  const cl = computeChecklistItems(info);
  const sections = [
    { title: "1. Identity & Service", items: cl.identity },
    { title: "2. Conditions", items: cl.conditions },
    { title: "3. Evidence Coverage (4 Proof Pillars)", items: cl.evidence },
    { title: "4. Statements", items: cl.statements },
    { title: "5. Submission Readiness", items: cl.submission },
  ];
  sections.forEach((sec) => {
    canvas.ensure(40);
    canvas.sectionTitle(sec.title);
    sec.items.forEach((it) => canvas.checkboxItem(!!it.ok, it.label, { size: 9 }));
    canvas.y -= 6;
  });
}

function buildPostFilingChecklist(canvas, info) {
  canvas.newPage("Post-Filing Checklist");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Post-Filing Checklist");
  canvas.para(
    "What to track AFTER you file. The clock starts the moment the VA stamps your application as received. Stay on top of the timeline to protect your effective date and avoid missed deadlines.",
    { size: 9 },
  );
  canvas.y -= 4;

  const cl = computeChecklistItems(info);
  canvas.sectionTitle("After You File");
  cl.postFiling.forEach((it) => canvas.checkboxItem(!!it.ok, it.label, { size: 9 }));

  canvas.y -= 6;
  canvas.sectionTitle("Key Decision-Review Deadlines");
  [
    "30 days from VA acknowledgement: respond to any 5103 \"Notice of Evidence Necessary\" letter",
    "1 year from a denial: file a Supplemental Claim (20-0995), Higher-Level Review (20-0996), or Board Appeal (10182) to preserve your effective date",
    "60 days for HLR / Board scheduling responses once you choose a lane",
  ].forEach((s) => canvas.bullet(s, { size: 9 }));
}

function buildFinalReminder(canvas, info) {
  canvas.newPage("Final Reminder");
  drawHeader(canvas, info);
  drawWatermark(canvas, canvas.isFree);
  canvas.y = PAGE_H - 60;
  canvas.heading("Final Reminder");
  canvas.para(
    "Before you submit anything to the VA:",
    { size: 11, bold: true },
  );
  canvas.y -= 4;
  [
    "Verify every form revision on VA.gov/find-forms — using an outdated form is the #1 cause of avoidable rejections.",
    "Sign and date every statement and form. Unsigned submissions are routinely returned.",
    "Keep a complete copy (PDF + paper) of everything you send.",
    "Consider a final review with a VA-accredited VSO, attorney, or claims agent. ClaimOps is a planning tool — it does not replace representation.",
    "Re-export this Claim Readiness Brief AFTER you close gaps so your filing copy reflects your final package.",
  ].forEach((s) => canvas.bullet(s, { size: 10 }));

  canvas.y -= 10;
  // Encouragement callout
  canvas.ensure(60);
  const cY = canvas.y;
  canvas.rect(M, cY - 50, PAGE_W - M * 2, 50, { fill: rgb(0.95, 0.97, 1.0), stroke: C_BRAND });
  canvas.rect(M, cY - 50, 4, 50, { fill: C_BRAND });
  canvas.text("YOU HAVE EARNED THESE BENEFITS", M + 16, cY - 16, { size: 9, bold: true, color: C_BRAND });
  canvas.para(
    "Filing a VA disability claim is a marathon, not a sprint. Trust the process, document everything, and don't go it alone. " +
    "Your service mattered — your evidence package is how you make sure the VA understands that.",
    { size: 9.5, indent: 16, width: 92 },
  );
  // place inside the box: re-anchor
  canvas.y = cY - 50 - 6;
}

function buildDisclaimer(canvas, info) {
  canvas.newPage("Disclaimer");
  drawHeader(canvas, info);
  // No watermark on disclaimer - we want it readable
  canvas.y = PAGE_H - 60;
  canvas.heading("Important Disclaimer", C_RED);
  canvas.para("Read carefully before relying on this document.", { color: C_MUTED, size: 10 });
  canvas.y -= 6;
  DISCLAIMER_PARAGRAPHS.forEach((p) => {
    canvas.para(p, { size: 10, width: 92 });
    canvas.y -= 4;
  });
  canvas.y -= 8;
  canvas.sectionTitle("About BeTheNexus.com");
  canvas.para(
    "BeTheNexus.com produced this claim planning workbook to help veterans organize evidence, draft statements, and prepare " +
    "for VA Compensation & Pension exams. This document is generated locally from data you entered. We do not store, " +
    "transmit, or review the contents of your claim package.",
  );
}

// ---- Public entrypoint ----
export async function createMasterClaimPdf({
  workspace, profile, financials, issues, evidence, master526ez, statements, combined, isPro = true,
}) {
  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const canvas = new PdfCanvas(pdfDoc, font, bold);

  const waiverCtx = { workspace, master526ez };
  const readiness = calculateReadiness(issues, evidence, waiverCtx);
  const gaps = getEvidenceGaps(issues, evidence, waiverCtx);
  const pillarsTotal = issues.length * evidenceElements.length;
  const pillarsCovered = issues.reduce(
    (sum, issue) => sum + evidenceElements.filter((el) => computePillarStatus(issue, el.id, evidence, waiverCtx) === "have").length,
    0,
  );
  const va = estimateVACompensation(combined?.rounded || 0, profile?.maritalStatus, profile?.minorDependents);
  const retired = estimateHigh3Retirement(financials?.high3Average, financials?.yearsService, financials?.monthsService);
  const rawCombined = combineRatings(issues.map((i) => Number(i.estimatedRating || 0))).raw;

  const info = {
    workspaceTitle: workspace?.title || "VA Claim Workspace",
    claimType: workspace?.claimType || "",
    veteranName: master526ez?.veteranName || profile?.name || "Veteran",
    email: master526ez?.email || "",
    generatedAt: new Date().toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" }),
    issues,
    evidence,
    statements: statements || [],
    waiverCtx,
    readiness,
    pillarsTotal,
    pillarsCovered,
    gaps,
    criticalGaps: gaps.length,
    weakGaps: 0,  // legacy field; not used post 2-state migration
    combinedRating: combined?.rounded || 0,
    rawCombined,
    bilateralApplied: !!combined?.bilateralApplied,
    bilateralBase: combined?.bilateralBase || 0,
    bilateralCombinedWithFactor: combined?.bilateralCombinedWithFactor || 0,
    estMonthly: va.monthly,
    va,
    retired,
  };

  buildCoverPage(canvas, info);
  // Set free-flag on canvas so each page builder draws the correct watermark
  canvas.isFree = !isPro;
  buildExecutiveSummary(canvas, info);          // 2
  buildTopPriorities(canvas, info);             // 3
  buildChecklistSummary(canvas, info);          // 4 Quick Readiness Checklist
  buildConditionReadinessSummary(canvas, info); // 5
  buildConditionMatrix(canvas, info);           // 6
  buildVaMathSummary(canvas, info);             // 7 VA Math Estimate
  buildEvidenceIndex(canvas, info);             // 8
  buildMissingPlan(canvas, info);               // 9
  buildStatementPacket(canvas, info);           // 10
  buildCpPrep(canvas, info);                    // 11
  buildPreFilingChecklist(canvas, info);        // 12
  buildPostFilingChecklist(canvas, info);       // 13
  buildFinalReminder(canvas, info);             // 14
  buildDisclaimer(canvas, info);                // 15

  // Footers (page numbers) — applied to every page except cover gets a softer treatment
  const total = canvas.pages.length;
  canvas.pages.forEach((p, idx) => {
    canvas.page = p.page;
    canvas.section = p.section;
    if (idx > 0) drawFooter(canvas, idx + 1, total, info);
    else {
      // Cover footer: just a brand strip at the bottom (no page number)
      p.page.drawLine({ start: { x: M, y: 60 }, end: { x: PAGE_W - M, y: 60 }, thickness: 0.6, color: C_BORDER });
    }
  });

  return await pdfDoc.save();
}
